import { ChangeEventHandler, useMemo, useState } from 'react'

import { isEqual } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { SingleValue } from 'react-select'
import { connect, useDataItem } from 'scrivito'

import { FollowUpSublistItem } from './FollowUpSublistItem'
import { useFollowUpsTabData } from './useFollowUpsTabData'
import { LIST_SIZE } from '../../const'
import { TabEmptyState } from '../../SharedTabs/TabEmptyState/TabEmptyState'
import { StringText } from '@/Atomic/Atoms/StringText'
import { Button } from '@/Atomic/Molecules/Button'
import { Search } from '@/Atomic/Molecules/Inputs/Search'
import { Option } from '@/Atomic/Molecules/Inputs/Select/types'
import { SimpleSelect } from '@/Atomic/Molecules/SimpleSelect'
import { ScrollablePanel } from '@/Atomic/Templates/ScrollablePanel'
import { Sublist, SublistFooter } from '@/Atomic/Templates/Sublist'
import { TimelineSection } from '@/Atomic/Templates/TimelineSection'
import { DataClassFollowUpActivityDrawer } from '@/Atomic/Widgets/DataClassDrawer/DataClass.FollowUpActivityDrawer'
import { DataItemDrawer } from '@/Atomic/Widgets/DataItemDrawer'
import { ASC_ORDER, DESC_ORDER, DataClassName } from '@/Data/const'
import { SchemaAttributes, DataScopeOrder } from '@/Data/types'
import { useCanvas } from '@/hooks/useCanvas'
import { useFlashSublistItem } from '@/hooks/useFlashSublistItem'
import { useDataItemDrawer } from '@/utils/CRUD/hooks/useDataItemDrawer'
import { getEntityLabelKey } from '@/utils/getEntityLabelKey'
import { getSchema } from '@/utils/getSchema'
import { getLocalizedSchemaAttributes } from '@/utils/schemaLocalization'
import {
  groupItemsByTimelineDate,
  parseDateAttribute,
} from '@/utils/timelineDateGroups'

interface FollowUpsTabProps {
  searchInputName: string
}

export const FollowUpsTab: React.FC<FollowUpsTabProps> = connect(
  ({ searchInputName }) => {
    const { t } = useTranslation()
    const dataItem = useDataItem()
    const subjectId = dataItem?.id() ?? ''

    const [search, setSearch] = useState('')
    const [order, setOrder] = useState<DataScopeOrder>(DESC_ORDER)

    const { drawerItem, setDrawerItem, isDrawerOpen, openDrawer, closeDrawer } =
      useDataItemDrawer()
    const { isCanvasOpen, openCanvas, closeCanvas } = useCanvas()
    const { flashId: flashItemId, flash: flashItemIdNow } =
      useFlashSublistItem()

    const { schema } = getSchema('activity')
    const schemaAttributes = getLocalizedSchemaAttributes(
      schema?.get('attributes') as SchemaAttributes,
    )
    const types = schemaAttributes?.['type']?.enumValues
    const getTypeTitle = (typeValue: string): string => {
      if (!types) return typeValue
      const match = types.find(({ value }) => value === typeValue)
      return match?.title ?? typeValue
    }

    const onSearchChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      setSearch(event.target.value)
    }

    const orderOptions: Option[] = [
      { label: t('common.sort.recentFirst'), value: 'desc' },
      { label: t('common.sort.recentLast'), value: 'asc' },
    ]
    const currentOrderValue = isEqual(order, DESC_ORDER) ? 'desc' : 'asc'
    const selectedOrderOption = orderOptions.find(
      (o) => o.value === currentOrderValue,
    )
    const handleOrderChange = (newValue: SingleValue<Option | unknown>) => {
      const val = (newValue as Option)?.value
      setOrder(val === 'asc' ? ASC_ORDER : DESC_ORDER)
    }

    const { items, hasMore, loadMore, count } = useFollowUpsTabData({
      subjectId,
      search,
      order,
      listSize: LIST_SIZE,
    })

    const timelineGroups = useMemo(
      () =>
        groupItemsByTimelineDate({
          items,
          getDate: (item) => parseDateAttribute(item.followUp.get('updatedAt')),
          t,
        }),
      [items, t],
    )

    if (!subjectId || !dataItem) return

    const header = (
      <div className="tab-header-wrapper">
        <div className="search-filter">
          <Search
            name={searchInputName}
            onChange={onSearchChange}
            searchWrapperClassName="tab-header-search"
            placeholder={t('search.title')}
          />
          <StringText value={t('common.sortBy')} />
          <SimpleSelect
            name="order-select"
            className="small"
            options={orderOptions}
            value={selectedOrderOption}
            onChange={handleOrderChange}
          />
        </div>
        <div className="tab-header-actions">
          <Button
            title={t('common.create')}
            size="sm"
            iconSize="sm"
            iconColor="white"
            icon="add"
            onClick={openCanvas}
          />
        </div>
      </div>
    )

    return (
      <>
        <Sublist>
          <ScrollablePanel header={header}>
            {count === 0 ? (
              <div className="sublist-wrapper">
                {search ? (
                  <TabEmptyState
                    title={t('tabs.noResults.title')}
                    description={t('tabs.noResults.searchCreate', {
                      entity: t(getEntityLabelKey(DataClassName.FOLLOW_UP), {
                        count: 1,
                      }),
                    })}
                    cta={{
                      label: t('common.create'),
                      icon: 'add',
                      onClick: openCanvas,
                    }}
                  />
                ) : (
                  <TabEmptyState
                    title={t('tabs.empty.followUps.title')}
                    description={t('tabs.empty.followUps.description')}
                    cta={{
                      label: t('common.create'),
                      icon: 'add',
                      onClick: openCanvas,
                    }}
                  />
                )}
              </div>
            ) : (
              <div className="sublist-wrapper">
                {timelineGroups.map((group) => (
                  <TimelineSection key={group.id} title={group.label}>
                    {group.items.map((item) => (
                      <FollowUpSublistItem
                        key={item.id}
                        followUp={item.followUp}
                        flash={item.id === flashItemId}
                        getTypeTitle={getTypeTitle}
                        onOpenFollowUp={(followUp) => {
                          setDrawerItem(followUp)
                          openDrawer()
                        }}
                      />
                    ))}
                  </TimelineSection>
                ))}
                <SublistFooter
                  amount={items.length}
                  total={count}
                  labelKey={getEntityLabelKey(DataClassName.FOLLOW_UP)}
                  onLoadMore={hasMore ? loadMore : undefined}
                />
              </div>
            )}
          </ScrollablePanel>
        </Sublist>
        {drawerItem && (
          <DataItemDrawer
            key={drawerItem.id()}
            isOpen={isDrawerOpen}
            close={closeDrawer}
            dataItem={drawerItem}
          />
        )}
        <DataClassFollowUpActivityDrawer
          originActivity={dataItem}
          isOpen={isCanvasOpen}
          close={closeCanvas}
          navigateOnCreated={false}
          onCreated={(followUpActivity) =>
            flashItemIdNow(followUpActivity.id())
          }
        />
      </>
    )
  },
)
